import { useEffect, useState } from "react";
import { Inbox, BadgePoundSterling } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { PageHeader, FilterBar, SummaryTile } from "@/components/shared/PageChrome";
import { ViewTabs, SearchInput, useListParams, InfoBanner } from "@/components/shared/ListChrome";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TermsFields, EMPTY_TERMS, termsPayload, termsComplete } from "@/components/offers/OfferTerms";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const VIEWS = [{ value: "ALL", label: "All open requests" }, { value: "TO_QUOTE", label: "To quote" }, { value: "OFFERED", label: "Offer submitted" }];
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function SupplierRequests() {
  const { view, q, page, setView, setQ, setPage } = useListParams();
  const [data, setData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ property_id: "", notes: "", ...EMPTY_TERMS });

  const load = () => api.get("/supplier/enquiries", { params: { view, q: q || undefined, page, page_size: 25 } }).then((r) => setData(r.data)).catch(() => setError(true));
  useEffect(() => { load(); }, [view, q, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/supplier/properties").then((r) => setProperties(r.data.items)).catch(() => {}); }, []);

  const submit = async () => {
    try {
      await api.post(`/supplier/enquiries/${active.id}/offers`, { property_id: form.property_id || null, notes: form.notes || null, ...termsPayload(form) });
      toast.success("Offer submitted to Jigsaw");
      setActive(null); setForm({ property_id: "", notes: "", ...EMPTY_TERMS }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit offer"); }
  };

  if (error) return <ErrorState testId="supplier-requests-error" />;

  return (
    <div data-testid="supplier-requests-page" className="space-y-5">
      <PageHeader title="Requests from Jigsaw" subtitle="Enquiries currently open for sourcing. Submit a rate offer for Jigsaw to consider - client identity is never shared at this stage." />

      {data && (
        <div data-testid="supplier-requests-summary" className="grid grid-cols-3 gap-4">
          <SummaryTile testId="sup-req-open" label="Open requests" value={data.counts.ALL} />
          <SummaryTile testId="sup-req-to-quote" label="Awaiting your offer" value={data.counts.TO_QUOTE} tone={data.counts.TO_QUOTE ? "warning" : "default"} />
          <SummaryTile testId="sup-req-offered" label="Offers submitted" value={data.counts.OFFERED} tone="success" />
        </div>
      )}

      <ViewTabs testId="supplier-requests-views" tabs={VIEWS} value={view} onChange={setView} counts={data?.counts} />
      <FilterBar testId="supplier-requests-filter-bar" summary={data ? `${data.total} request${data.total === 1 ? "" : "s"} match` : null}>
        <SearchInput testId="supplier-requests-search" value={q} onChange={setQ} placeholder="Search reference, location or postcode…" />
      </FilterBar>

      {!data && <LoadingState testId="supplier-requests-loading" />}
      {data && data.items.length === 0 && <EmptyState testId="supplier-requests-empty" icon={Inbox} title={view === "ALL" && !q ? "No open requests right now" : "No requests in this view"} description={view === "ALL" && !q ? "Jigsaw opens requests to suppliers while sourcing; you will see them here." : "Try another view or clear the search."} />}
      {data && data.items.length > 0 && (
        <div className="space-y-3">
          <StackedList testId="supplier-requests-stacked" items={data.items} title={(e) => `${e.jigsaw_reference} · ${e.location_text}`} status={(e) => (e.my_offer ? <StatusBadge status={e.my_offer.status} /> : <StatusBadge status="TO_QUOTE" />)}
            onOpen={(e) => setActive(e)} canOpen={(e) => !e.my_offer} openLabel="Submit offer"
            fields={[{ label: "Dates", value: (e) => `${e.arrival_date || "—"} → ${e.departure_date || "—"}`, mono: true, full: true }, { label: "Rooms / guests", value: (e) => `${e.rooms_required} / ${e.guests_count}` }, { label: "Nights", value: (e) => e.nights }, { label: "Your offer", value: (e) => (e.my_offer?.rate_amount ? `£${e.my_offer.rate_amount}` : "—"), mono: true, full: true }]} />
          <div className="hidden sm:block">
          <Table data-testid="supplier-requests-table">
            <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Location</TableHead><TableHead>Dates</TableHead><TableHead>Nights</TableHead><TableHead>Rooms / guests</TableHead><TableHead>Priority</TableHead><TableHead>Your offer</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.map((e) => (
                <TableRow key={e.id} data-testid={`supplier-request-row-${e.id}`}>
                  <TableCell className="font-mono-ref font-semibold text-jigsaw-navy">{e.jigsaw_reference}</TableCell>
                  <TableCell>{e.location_text}{e.postcode && <span className="ml-1 text-[12px] text-jigsaw-muted">{e.postcode}</span>}</TableCell>
                  <TableCell className="whitespace-nowrap text-[13px]">{fmt(e.arrival_date)} &ndash; {fmt(e.departure_date)}</TableCell>
                  <TableCell>{e.nights ?? "—"}</TableCell>
                  <TableCell>{e.rooms_required} <span className="text-jigsaw-muted">/ {e.guests_count}</span></TableCell>
                  <TableCell><StatusBadge status={e.priority} /></TableCell>
                  <TableCell>{e.my_offer ? <span className="flex items-center gap-2"><span className="font-mono-ref text-jigsaw-navy">£{e.my_offer.rate_amount}</span><StatusBadge status={e.my_offer.status} /></span> : <span className="text-[12px] text-jigsaw-muted">Not yet quoted</span>}</TableCell>
                  <TableCell className="text-right">{!e.my_offer && <Button data-testid={`submit-offer-${e.id}`} size="sm" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark" onClick={() => setActive(e)}><BadgePoundSterling className="mr-1 h-3.5 w-3.5" /> Submit offer</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="supplier-requests-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}

      <InfoBanner testId="supplier-requests-banner" icon={Inbox} title="Offers are reviewed by Jigsaw" text="Your rate is compared with other suppliers before anything is presented to the client. Decisions appear on your home page activity stream." />

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Submit an offer - {active?.jigsaw_reference}</DialogTitle></DialogHeader>
          {active && <p className="text-[13px] text-jigsaw-muted">{active.location_text} · {fmt(active.arrival_date)} – {fmt(active.departure_date)} · {active.rooms_required} room{active.rooms_required === 1 ? "" : "s"}, {active.guests_count} guest{active.guests_count === 1 ? "" : "s"}</p>}
          <div className="space-y-3">
            <div>
              <Label>Property (optional)</Label>
              <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="offer-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not specified</SelectItem>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <TermsFields form={form} setForm={setForm} idPrefix="offer" />
            <div><Label>Notes</Label><Textarea data-testid="offer-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="offer-save-button" onClick={submit} disabled={!termsComplete(form)}>Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
